import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatUsd } from '@ithildin/shared';

import { api } from '../lib/api';
import { useSession } from '../hooks/useSession';
import { shortAddress } from '../lib/format';
import { ReplyRecord } from '../components/ReplyRecord';

/**
 * The public face of a wallet, and the only page someone can be sent to from
 * outside. Works signed out: a stranger arriving from a bio link should see the
 * price and the track record before being asked to connect anything.
 */
export function Profile() {
  const { handle = '' } = useParams();
  const navigate = useNavigate();
  const { profile: me } = useSession();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['resolve', handle],
    queryFn: () => api.resolve(handle),
    retry: false,
  });

  if (isLoading) return <div className="page page-narrow" />;

  if (isError || !data) {
    return (
      <div className="page page-narrow">
        <div className="empty">
          <p style={{ marginTop: 0, color: 'var(--bone-2)' }}>
            No wallet found for <span className="addr">{handle}</span>.
          </p>
          <p className="hint">Names must be a .eth or .sol you own, or a full address.</p>
        </div>
      </div>
    );
  }

  const p = data.profile;
  const price = BigInt(p.inboxPriceMicros);
  const isMe = me?.address === data.address;

  return (
    <div className="page page-narrow">
      <header className="profile-head">
        <h1 className="profile-name">
          {p.displayName ?? data.name ?? shortAddress(data.address, 6, 6)}
        </h1>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          {data.name && <span className="addr">{data.name}</span>}
          <span className="addr" title={data.address}>
            {shortAddress(data.address, 6, 6)}
          </span>
          <span className="addr">on {data.chain}</span>
        </div>
        {p.bio && <p className="profile-bio">{p.bio}</p>}
      </header>

      <div className="profile-price">
        <div>
          <span className="eyebrow">To open a thread</span>
          <div className="meter-amount" style={{ fontSize: '2rem', marginTop: 6 }}>
            {price > 0n ? formatUsd(p.inboxPriceMicros) : 'Free'}
          </div>
          {price > 0n && (
            <p className="hint" style={{ marginTop: 8, maxWidth: '38ch' }}>
              Held in escrow. Returned to you in full if they don't reply within{' '}
              {p.replyWindowHours}h.
            </p>
          )}
        </div>

        <div className="stack" style={{ gap: 10, alignItems: 'flex-end' }}>
          {p.paused ? (
            <span className="pill-paused">Not taking paid messages</span>
          ) : isMe ? (
            <Link to="/settings" className="btn">Edit your profile</Link>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/compose?to=${encodeURIComponent(data.name ?? data.address)}`)}
            >
              {price > 0n ? `Message for ${formatUsd(p.inboxPriceMicros)}` : 'Send a message'}
            </button>
          )}
          {p.freeForCollection && (
            <span className="hint" style={{ textAlign: 'right', maxWidth: '30ch' }}>
              Free for holders of{' '}
              <span className="addr">{shortAddress(p.freeForCollection, 4, 4)}</span>
            </span>
          )}
        </div>
      </div>

      <ReplyRecord record={p.record} windowHours={p.replyWindowHours} />

      <p className="hint" style={{ marginTop: 22 }}>
        {p.reachable
          ? 'This wallet gets notified when a message arrives.'
          : 'This wallet has no notifications set up, so they will see your message the next time they visit.'}
      </p>
    </div>
  );
}
